import { FieldTypes, StandardFormFieldDto } from '@app/models/dtos/form';
import AddressField from '@app/views/molecules/responder-form-fields/address-field';
import CurrencyField from '@app/views/molecules/responder-form-fields/currency-field';
import DateField from '@app/views/molecules/responder-form-fields/date-field';
import DateRangeField from '@app/views/molecules/responder-form-fields/date-range-field';
import DayField from '@app/views/molecules/responder-form-fields/day-field';
import SignatureField from '@app/views/molecules/responder-form-fields/signature-field';
import TagsField from '@app/views/molecules/responder-form-fields/tags-field';
import TimeField from '@app/views/molecules/responder-form-fields/time-field';
import LinearRatingField from '@app/views/molecules/responder-form-fields/linear-rating';
import RatingField from '@app/views/molecules/responder-form-fields/rating-field';

import { useDialogModal } from '@app/lib/hooks/use-dialog-modal';
import { cn } from '@app/shadcn/util/lib';
import { useBuilderActions } from '@app/store/jotai/field-selectors';
import DeleteIcon from '@Components/icons/delete';
import { SwitchIcon } from '@Components/icons/switch-icon';
import { firstPartyImageProps } from '@app/utils/first-party-image';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import CalculatedFieldBuilder from './calculated-field-builder';
import DropDownField from './drop-down-field';
import FileUpload from './file-upload-field';
import ImageField from './image-field';
import InputField from './input-field';
import MatrixFieldBuilderWrapper from './matrix-field-builder-wrapper';
import RepeatingGroupBuilder from './repeating-group-builder';
import { isRepeatingGroup } from '@app/utils/repeating-groups';
import TabularInputFieldBuilderWrapper from './tabular-input-field-builder-wrapper';
import VideoField from './video-field';
import YesNoField from './yes-no-field';

export const RenderImage = ({ field, isBuilder = false }: { field: StandardFormFieldDto; isBuilder?: boolean }) => {
    const { t } = useTranslation('builder');
    const { updateFieldImage } = useBuilderActions();
    const { openDialogModal } = useDialogModal();
    const handleRemoveImage = () => {
        updateFieldImage('');
    };
    const handleChangeImage = () => {
        openDialogModal('UNSPLASH_IMAGE_PICKER', {
            updatePageImage: updateFieldImage
        });
    };
    return field?.imageUrl ? (
        <div className={`relative my-4 max-h-[168px] w-full ${isBuilder ? 'group' : ''}`}>
            {/* Builder only: shown on hover, and while a button has keyboard focus. */}
            {isBuilder && (
                <div className={cn('absolute z-10 flex h-full w-full items-start justify-start gap-4 p-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100')}>
                    <button type="button" aria-label={t('CANVAS.REMOVE_IMAGE')} title={t('CANVAS.REMOVE_IMAGE')} className="shadow-large cursor-pointer rounded-md bg-white p-2" onClick={handleRemoveImage}>
                        <DeleteIcon width={16} height={16} aria-hidden="true" />
                    </button>
                    <button type="button" aria-label={t('CANVAS.CHANGE_IMAGE')} title={t('CANVAS.CHANGE_IMAGE')} className="shadow-large cursor-pointer rounded-md bg-white p-2" onClick={handleChangeImage}>
                        <SwitchIcon width={16} height={16} aria-hidden="true" />
                    </button>
                </div>
            )}
            <Image style={{ objectFit: 'contain', width: 'fit-content', maxHeight: '168px' }} sizes="100vw" {...firstPartyImageProps(field.imageUrl)} alt="" height={0} width={0} priority />
        </div>
    ) : (
        <></>
    );
};

function renderFieldWrapper(field: StandardFormFieldDto, slide: StandardFormFieldDto, disabled: boolean) {
    return (
        <div className="relative h-full w-full space-y-2">
            <RenderImage field={field} isBuilder />
            {renderField(field, slide, disabled)}
        </div>
    );
}

function renderField(field: StandardFormFieldDto, slide: StandardFormFieldDto, disabled: boolean) {
    switch (field.type) {
        case FieldTypes.EMAIL:
        case FieldTypes.NUMBER:
        case FieldTypes.SHORT_TEXT:
        case FieldTypes.LONG_TEXT:
        case FieldTypes.LINK:
        case FieldTypes.PHONE_NUMBER:
            return <InputField field={field} slide={slide} disabled={disabled} />;
        case FieldTypes.FILE_UPLOAD:
            return <FileUpload field={field} slide={slide} disabled={disabled} />;
        case FieldTypes.YES_NO:
            return <YesNoField field={field} slide={slide} disabled={disabled} />;
        case FieldTypes.DROP_DOWN:
        case FieldTypes.MULTIPLE_CHOICE:
            return <DropDownField field={field} slide={slide} disabled={disabled} />;
        case FieldTypes.TEXT:
            return <></>;
        case FieldTypes.RATING:
            return <RatingField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.DATE:
            return <DateField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.DAY:
            return <DayField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.TIME:
            return <TimeField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.CURRENCY:
            return <CurrencyField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.DATE_RANGE:
            return <DateRangeField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.TAGS:
            return <TagsField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.ADDRESS:
            return <AddressField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.SIGNATURE:
            return <SignatureField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.LINEAR_RATING:
            return <LinearRatingField field={field} slide={slide} isBuilder={true} />;
        case FieldTypes.IMAGE_CONTENT:
            return <ImageField isBuilder field={field} slide={slide} />;
        case FieldTypes.VIDEO_CONTENT:
            return <VideoField isBuilder field={field} slide={slide} />;
        case FieldTypes.MATRIX:
            return <MatrixFieldBuilderWrapper field={field} />;
        case FieldTypes.TABULAR_INPUT:
            return <TabularInputFieldBuilderWrapper field={field} />;
        case FieldTypes.CALCULATED:
            return <CalculatedFieldBuilder field={field} />;
        case FieldTypes.GROUP:
            return isRepeatingGroup(field) ? <RepeatingGroupBuilder field={field} slide={slide} disabled={disabled} /> : null;
        default:
            return null;
    }
}

export default renderFieldWrapper;
